import { Col, Flex } from "@amzn/stencil-react-components/layout";
import { useBreakpoints } from "@amzn/stencil-react-components/responsive";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlumniMessageBanner } from "../../components/AlumniBanner";
import { AppointmentCard } from "../../components/appointment-card";
import AppLoader from "../../components/common/AppLoader";
import { Gratitude } from "../../components/gratitude";
import { ManageApplicationCard } from "../../components/jobDetailsCard";
import { ScreeningResults } from "../../components/screening-results/screeningResults";
import { TaskGroupAggregator, TasksGroup } from "../../components/task";
import { SUPPORTED_COUNTRY } from "../../config/appConfigConstants";
import { HVH_COLORS } from "../../config/palette";
import { GetChecklistTasks, TaskGroup, getTargetTaskGroup, hideTaskOnConfig } from "../../config/tasks";
import { buildUrl } from "../../config/urls";
import { Stage } from "../../helpers/get-settings";
import { isDragonstone } from "../../helpers/url-params";
import {
  badgePhotoAllowedStateForJapan,
  hasSkipAppointmentOneEligibility,
  isAlumni,
  isValidToShowGratitudeComponent,
} from "../../helpers/utils";
import { useChecklistArb } from "../../hooks/use-arb";
import { useConfig } from "../../hooks/useConfig";
import { apiSlice } from "../../reduxStore/api/apiSlice";
import { useAppSelector } from "../../reduxStore/reduxHooks";
import { getConfigData } from "../../reduxStore/slices/configSlice";
import { isCustomerService } from "../../utility/app-config-helper";
import { getLocale, getCountryForDomain } from "../../utility/locale-helper";
import { NewHireVideoFlyout } from "../../components/new-hire-video/NewHireVideo";
import { useGetApplicationManageData } from "../../hooks/apis/useGetApplicationManageData";
// import { useGetTasks } from "../../hooks/useGetTasks/useGetTasks";
// import { getTask } from "../../reduxStore/slices/taskSlice";
// import { TaskGroupAggregatorV2 } from "../../components/task/task-v2";

interface ChecklistRouteProps {
  stage?: Stage;
  isNewHireVideoRoute?: boolean;
}

export interface rtwAppointmentProps {
  isRtwAppointmentEnabled?: boolean;
  PreviousrightToWorkExpired: boolean | undefined;
}

export const ChecklistRoute = ({ stage, isNewHireVideoRoute }: ChecklistRouteProps) => {
  const { rtwAppointmentInfo, gratitudeComponentState, caasI9Status, caasRTWStatus } = useAppSelector(getConfigData);
  // const [isTasksLoaded, setIsTasksLoaded] = useState(false);
  // const tasks = useAppSelector(getTask);
  const { applicationId, requisitionId, candidateId } = useParams<{
    applicationId: string;
    requisitionId: string;
    candidateId: string;
  }>();

  const country = getCountryForDomain();
  const { appConfigLoaded, isConfigLoaded, isErrorLoadingConfig } = useConfig();

  if (isErrorLoadingConfig) {
    throw new Error("Have an error occured when loading initial data");
  }

  const applicationManageData = useGetApplicationManageData();

  const skipAppointmentOneEligiblilty = hasSkipAppointmentOneEligibility(applicationManageData);
  const enableBadgePhoto = badgePhotoAllowedStateForJapan(applicationManageData);
  const isAlumniCandidate = isAlumni(applicationManageData);
  const bundle = useChecklistArb();

  // const { getTasks } = useGetTasks();

  // useEffect(() => {
  //   if (isConfigLoaded && !isTasksLoaded) {
  //     getTasks();
  //     setIsTasksLoaded(true);
  //   }
  // }, [isConfigLoaded, isTasksLoaded, getTasks]);
  const isEligibleForI9DocInsteadRTW = caasI9Status !== "SKIPPED";

  const { taskGroupForCustomerService, taskGroupForNewUI, taskGroupForSkipAppt1Eligible } = GetChecklistTasks({
    bundle,
    applicationId,
    appConfigLoaded,
    country,
    rightToWorkAppointmentProps: rtwAppointmentInfo,
    workAuthStatus: isEligibleForI9DocInsteadRTW ? caasI9Status : caasRTWStatus,
    skipAppt1: skipAppointmentOneEligiblilty,
    enableBadgePhotoForJapan: enableBadgePhoto,
  });

  const targetTaskGroup = getTargetTaskGroup({
    isCustomerService: isCustomerService(applicationId),
    isDragonstone: isDragonstone(requisitionId),
    skipAppointmentOne: skipAppointmentOneEligiblilty,
    taskGroups: {
      taskGroupForCustomerService,
      taskGroupForNewUI,
      taskGroupForSkipAppt1Eligible,
    },
  });

  const isDragonStonePage = isDragonstone(requisitionId) || isCustomerService(applicationId);
  const groups = targetTaskGroup.map((taskGroup) => {
    return {
      ...taskGroup,
      tasks: taskGroup.tasks.filter(hideTaskOnConfig).map((task) => ({
        ...task,
        titleStatusPath:
          task.titleStatusPath &&
          buildUrl(task.titleStatusPath, {
            stage,
            requisitionId,
            applicationId,
            candidateId,
          }),
      })),
    };
  });

  const {
    matches: { m: isMediumViewport, s: isSmallViewport },
  } = useBreakpoints();
  const isLargeViewport = !isSmallViewport && !isMediumViewport;

  if (!appConfigLoaded || !gratitudeComponentState || !isConfigLoaded) {
    return <AppLoader />;
  }

  if (
    isValidToShowGratitudeComponent({
      gratitudeComponentState: gratitudeComponentState,
      country,
      isCustomerService: isCustomerService(applicationId),
    })
  ) {
    return <Gratitude gratitudeComponentState={gratitudeComponentState} stage={stage} />;
  }

  if (isNewHireVideoRoute) {
    return <NewHireVideoFlyout isTask={false} />;
  }

  return (
    <Col id={"checklist-main-page"} backgroundColor={HVH_COLORS.NEUTRAL_10} data-testid="new-ui">
      {(isAlumniCandidate || skipAppointmentOneEligiblilty) && <AlumniMessageBanner />}
      {isSmallViewport && isDragonStonePage && (
        <Col alignItems="center">{appConfigLoaded && <ManageApplicationCard stage={stage} collapsed={true} />}</Col>
      )}
      <Col id={"page-header"} gridGap="2rem">
        {isDragonStonePage && (
          <Col alignItems="left">
            <AppointmentCard stage={stage} />
          </Col>
        )}
      </Col>
      <Col>{appConfigLoaded && isDragonStonePage && isSmallViewport && <ScreeningResults />}</Col>
      <Col id={"task-group-header"} alignItems="center">
        <Col
          color={HVH_COLORS.NEUTRAL_90}
          maxWidth={isLargeViewport ? "1500px" : "900px"}
          width={"100%"}
          gridGap="2rem"
          padding={isLargeViewport ? "1rem 1rem 4rem 1rem" : "0rem 0rem 4rem 0rem"}
        >
          <Flex
            flexDirection={isLargeViewport ? "row" : "column"}
            justifyContent={"space-evenly"}
            gridGap={isLargeViewport ? "1rem" : "0rem"}
          >
            <Col width={isLargeViewport ? "60%" : "100%"}>
              {/* {stage && <TaskGroupAggregatorV2 />} */}
              {stage ? <TaskGroupAggregator groups={groups} candidateId={candidateId} stage={stage} /> : null}
            </Col>
            <Col width={isLargeViewport ? "40%" : "100%"}>
              {appConfigLoaded && isDragonStonePage && !isSmallViewport && <ScreeningResults />}
              {appConfigLoaded && isDragonStonePage && !isSmallViewport && (
                <ManageApplicationCard stage={stage} collapsed={false} />
              )}
            </Col>
          </Flex>
        </Col>
      </Col>
    </Col>
  );
};
